import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import ThreeDotIcon from "../../SVG/three-dot-icon";
import { usePortal } from "../../hooks/use-portal";
import { PopupWithApi, type PopupWithApiProps } from "../popup-with-api/popup-with-api";
import "./dots-with-popup.scss";

const MENU_OFFSET = 6;
const VIEWPORT_PAD = 8;
const FALLBACK_MENU_W = 300;
const FALLBACK_MENU_H = 240;

export interface DotsWithPopupProps {
  /** Props for the panel (`PopupWithApi`). `onCancel` / `onMarkAsSuccess` are wrapped to support closing. */
  popup: PopupWithApiProps;
  className?: string;
  triggerClassName?: string;
  /** `aria-label` on the dots control. */
  triggerAriaLabel?: string;
  /** After a successful `onMarkAsSuccess` (no throw), close the popup. Default true. */
  closeOnMarkSuccess?: boolean;
  /** Controlled open state; pair with `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Portal root; default `document.body` so the menu escapes overflow:hidden ancestors. */
  getPortalContainer?: () => HTMLElement;
}

function computeFixedPosition(
  triggerRect: DOMRect,
  menuWidth: number,
  menuHeight: number
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = triggerRect.bottom + MENU_OFFSET;
  if (top + menuHeight > vh - VIEWPORT_PAD) {
    top = triggerRect.top - menuHeight - MENU_OFFSET;
  }
  top = Math.min(Math.max(VIEWPORT_PAD, top), vh - menuHeight - VIEWPORT_PAD);

  let left = triggerRect.right - menuWidth;
  if (left < VIEWPORT_PAD) {
    left = VIEWPORT_PAD;
  }
  if (left + menuWidth > vw - VIEWPORT_PAD) {
    left = vw - menuWidth - VIEWPORT_PAD;
  }

  return { top, left };
}

export function DotsWithPopup(props: DotsWithPopupProps) {
  const {
    popup,
    className = "",
    triggerClassName = "",
    triggerAriaLabel = "Open actions",
    closeOnMarkSuccess = true,
    open: controlledOpen,
    onOpenChange,
    getPortalContainer,
  } = props;

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const [fixedPos, setFixedPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const updatePosition = useCallback(() => {
    if (!open || !triggerRef.current) {
      setFixedPos(null);
      return;
    }
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const mw = menuRef.current?.offsetWidth || FALLBACK_MENU_W;
    const mh = menuRef.current?.offsetHeight || FALLBACK_MENU_H;
    setFixedPos(computeFixedPosition(triggerRect, mw, mh));
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setFixedPos(null);
      return;
    }
    updatePosition();
    const id = requestAnimationFrame(() => updatePosition());
    return () => cancelAnimationFrame(id);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [open, setOpen]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (!open) return;
      const t = event.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, setOpen]);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const { onCancel: popupOnCancel, onMarkAsSuccess: popupOnMarkAsSuccess, ...restPopup } = popup;

  const wrappedOnCancel = () => {
    popupOnCancel?.();
    setOpen(false);
  };

  const wrappedOnMarkAsSuccess = async () => {
    try {
      await popupOnMarkAsSuccess();
      if (closeOnMarkSuccess) {
        setOpen(false);
      }
    } catch {
      /* parent handles errors; keep menu open */
    }
  };

  const renderInPortal = usePortal(getPortalContainer);

  const dropdown =
    open ? (
      <div
        ref={menuRef}
        id={menuId}
        className="dots-with-popup__portal"
        role="presentation"
        style={{
          top: fixedPos?.top ?? 0,
          left: fixedPos?.left ?? 0,
          opacity: fixedPos == null ? 0 : 1,
          pointerEvents: fixedPos == null ? "none" : "auto",
        }}
      >
        <PopupWithApi {...restPopup} onCancel={wrappedOnCancel} onMarkAsSuccess={wrappedOnMarkAsSuccess} />
      </div>
    ) : null;

  return (
    <div className={`dots-with-popup ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className={`dots-with-popup__trigger ${triggerClassName}`.trim()}
        aria-label={triggerAriaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? menuId : undefined}
        onClick={handleTriggerClick}
      >
        <span className="dots-with-popup__icon">
          <ThreeDotIcon />
        </span>
      </button>
      {open ? renderInPortal(dropdown) : null}
    </div>
  );
}
